import { Schedule, ScheduleType } from '@robocorp/robocloud-scheduler-interface';
import { asyncForLoop } from '@robocorp/robocloud-shared-utils';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActions, useLocalStorage, useParams, useSelector } from '~/hooks';
import {
  useActivityTemplatesQuery,
  useCreateActivityMutation,
  useCreateActivityTemplateMutation,
  useCreateProcessMutation,
  useCreateScheduleMutation,
  useCreateSecretMutation,
  useEditActivityMutation,
  useEditProcessMutation,
  useImagesQuery,
  useLinkGitSourceMutation,
  useSecretsQuery,
  useStartProcessRunDefaultMutation,
} from '~/queries';
import {
  useCreateAssetMutation,
  useListAssetsQuery,
  useRequestAssetUploadMutation,
  useUploadAssetMutation,
} from '~/queries/useAssetStorageQuery';
import { AssetContent } from '~/schemas/assetStorageSchemas';
import { CreateAssetResponse } from '~/services/assetStorageAPI/assetStorageService';

export enum OnboardingProcessTypes {
  UPTIME_MONITORING = 'uptime_monitoring',
  WEB_STORE_ORDER_PROCESSOR = 'web_store_order_processor',
}

interface OnboardingProcess {
  type?: OnboardingProcessTypes;
  processId?: string;
  taskPackageId?: string;
  assetId?: string;
  secretId?: string;
}

type ReturnValues = {
  setup: (params: HandleSetupParams) => void;
};

export type Step = { title: string; description: React.ReactNode };

export type HandleSetupParams = {
  type: OnboardingProcessTypes;
};

const onboardingProcesses = {
  [OnboardingProcessTypes.UPTIME_MONITORING]: {
    name: 'Uptime Monitoring',
    assetName: 'websites_to_monitor',
    description: `
## This automation monitors your websites and notifies you if they are not online.
    
### It consists of two steps:
- The first step retrieves the list of websites to monitor in the asset storage and creates one work item for each website
- The second step consumes each work item (website) and requests the website. If the website is not online, the work item is failed.
    

Try adding your own website to the list in the asset storage!
`,
    assetContent: ['https://google.com'],
    assetDescription: 'List of websites to monitor',
    schedule: 'every week',
    taskPackageGithubURL: 'https://github.com/robocorp/template-uptime-monitoring',
    steps: [
      {
        name: 'Retrieve Websites to Monitor',
        task_name: 'retrieve_websites_to_ping',
      },
      { name: 'Monitor Website', task_name: 'ping_website' },
    ],
  },
  [OnboardingProcessTypes.WEB_STORE_ORDER_PROCESSOR]: {
    name: 'Web Store Order Processor',
    assetName: 'web_store_orders',
    description: `
## This automation processes orders from your web store.
    
### It consists of two steps:
- The first step splits the orders file into individual orders and creates one work item for each order
- The second step consumes each work item (order) and processes it
    
A work item was failed due to a typo: try finding it and retrying the work item!
`,
    assetContent: [
      `Name,Item,Zip
Sol Heaton,Sauce Labs Bolt T-Shirt,3695
Gregg Arroyo,Sauce Labs Onesie,4418
Zayaan Hurley,Sauce Labs Bolt T-Shirt,3013
Tyler-James Haigh,Test.allTheThings() T-Shirt (Red),4403
Camden Martin,Sauce Labs Bolt T-Shirt,1196
Zoya Roche,Sauce Labs Fleece Jacket,3127
Aasiyah Avila,Sauce Labs Onesie,3238
Nur Magana,Sauce Labs Bolt T-Shirt,2145
Neil Parkinson,Test.allTheThings() T-Shirt (Red),2401
`,
    ],
    secretName: 'swaglabs',
    assetDescription: 'List of web store orders',
    schedule: 'every week',
    taskPackageGithubURL: 'https://github.com/robocorp/example-web-store-work-items',
    steps: [
      {
        name: 'Split orders file',
        task_name: 'Split orders file',
      },
      {
        name: 'Load and Process All Orders',
        task_name: 'Load and Process All Orders',
      },
    ],
  },
};

export type GetStepsParams = {
  baseURL: string;
  taskPackageId: string | null;
  processId: string | null;
  assetId: string | null;
  type: OnboardingProcessTypes;
};

export const useOnboardingProcess = (): ReturnValues => {
  const navigate = useNavigate();

  const { toggleOnboardingCompletedDialog } = useActions(({ uiActions }) => ({
    toggleOnboardingCompletedDialog: uiActions.toggleOnboardingCompletedDialog,
  }));

  const { setCreatingOnboardingProcess } = useActions(({ uiActions }) => ({
    setCreatingOnboardingProcess: uiActions.setCreatingOnboardingProcess,
  }));

  const { mutateAsync: createProcessAsync } = useCreateProcessMutation();
  const { mutateAsync: editProcessAsync } = useEditProcessMutation();
  const { mutateAsync: createStepAsync } = useCreateActivityMutation();
  const { mutateAsync: editStepAsync } = useEditActivityMutation();
  const { mutateAsync: createRobotAsync } = useCreateActivityTemplateMutation();
  const { mutateAsync: linkPublicGithubAsync } = useLinkGitSourceMutation();
  const { mutateAsync: createAssetAsync } = useCreateAssetMutation({
    disableNotifications: true,
  });
  const { mutateAsync: uploadAssetAsync } = useUploadAssetMutation();
  const { mutateAsync: createScheduleAsync } = useCreateScheduleMutation();
  const { mutateAsync: requestAssetUploadAsync } = useRequestAssetUploadMutation();
  const { data: images = [] } = useImagesQuery();
  const { data: assetsList = { data: [] } } = useListAssetsQuery();
  const { data: taskPackagesList = [] } = useActivityTemplatesQuery();
  const { mutateAsync: startProcessRunAsync } = useStartProcessRunDefaultMutation();

  const { data: secrets = [] } = useSecretsQuery();
  const { mutateAsync: createSecretAsync } = useCreateSecretMutation();

  const { organizationShortName, workspaceShortName } = useParams();

  const cloudWorker = useMemo(() => ({ id: images[0]?.id ?? null, type: 'container' }), [images]);

  const currentUser = useSelector((state) => state.userResources.users.current);

  const baseURL = useMemo(() => {
    return `/${organizationShortName}/${workspaceShortName}`;
  }, [organizationShortName, workspaceShortName]);

  const saveOnboardingProcess = ({
    type,
    processId,
    taskPackageId,
    assetId,
    secretId,
  }: OnboardingProcess) => {
    const currentLocalStorage = JSON.parse(
      window.localStorage.getItem('onboardingProcess') ?? '{}',
    );

    window.localStorage.setItem(
      'onboardingProcess',
      JSON.stringify({
        type: type || currentLocalStorage.type,
        processId: processId || currentLocalStorage.processId,
        taskPackageId: taskPackageId || currentLocalStorage.taskPackageId,
        assetId: assetId || currentLocalStorage.assetId,
        secretId: secretId || currentLocalStorage.secretId,
      }),
    );
  };

  const handleSetup = useCallback(
    async (params: HandleSetupParams) => {
      const { type } = params;
      if (
        type !== OnboardingProcessTypes.UPTIME_MONITORING &&
        type !== OnboardingProcessTypes.WEB_STORE_ORDER_PROCESSOR
      ) {
        return;
      }

      setCreatingOnboardingProcess();

      // 1. "Process creation"
      const createdProcess = await createProcessAsync({
        name: onboardingProcesses[type].name,
        description: onboardingProcesses[type].description,
        explicitOutput: true,
        webhookConfigs: [],
      });

      saveOnboardingProcess({
        type,
        processId: createdProcess.id,
      });

      const processURL = `${baseURL}/processes/${createdProcess.id}`;

      toggleOnboardingCompletedDialog();
      navigate(`${processURL}/onboarding`);

      // 2. "Automation code upload"
      const createdTaskPackage = await (async () => {
        const existingTaskPackage =
          taskPackagesList.find(
            (taskPackage) => taskPackage.name === onboardingProcesses[type].name,
          ) ?? null;

        if (existingTaskPackage) {
          return existingTaskPackage;
        }
        const taskPackage = await createRobotAsync({
          name: onboardingProcesses[type].name,
        });

        saveOnboardingProcess({
          taskPackageId: taskPackage.id,
        });

        await linkPublicGithubAsync({
          robotId: taskPackage.id,
          gitUrl: onboardingProcesses[type].taskPackageGithubURL,
        });

        return taskPackage;
      })();

      // 3. "Process configuration"
      await asyncForLoop(onboardingProcesses[type].steps, async (step) => {
        const activity = await createStepAsync({
          processId: createdProcess.id,
          activity: {
            name: step.task_name,
            templateId: createdTaskPackage.id,
            processId: createdProcess.id,
            runtimeType: cloudWorker.type,
            containerImageId: cloudWorker.id,
          },
        });

        await editStepAsync({
          processId: createdProcess.id,
          activityId: activity.id,
          activity: {
            ...activity,
            name: step.name,
          },
        });
      });

      setCreatingOnboardingProcess();
      // 4. "Setting up notifications"
      await editProcessAsync({
        processId: createdProcess.id,
        process: {
          ...createdProcess,
          config: {
            ...createdProcess.config,
            emailNotifications: {
              failedRuns: [currentUser.details?.email ?? ''],
              successfulRuns: [],
            },
          },
        },
      });

      // 5. if type === 'uptime_monitoring' -> "Creating an asset with the list of websites"
      // 5. if type === 'web_store_order_processor' -> "Creating an asset with the list of items to order and a vault file with the credentials to access the swag store"
      const existingAsset =
        assetsList.data.find((asset) => asset.alias === onboardingProcesses[type].assetName) ??
        null;

      const createdAsset = await (async () => {
        if (existingAsset) {
          return existingAsset;
        }

        const asset: CreateAssetResponse = await createAssetAsync({
          alias: onboardingProcesses[type].assetName,
          description: onboardingProcesses[type].assetDescription,
          contentType: 'text/plain',
          assetContent: AssetContent.Text,
        });

        saveOnboardingProcess({
          assetId: asset.assetId,
        });

        return asset;
      })();

      const assetUploadURL = await requestAssetUploadAsync({
        contentType: 'text/plain',
        assetId: createdAsset.assetId,
      });

      const file = new File(
        onboardingProcesses[type].assetContent,
        onboardingProcesses[type].assetName,
        {
          type: 'text/plain',
        },
      );

      await uploadAssetAsync({
        contentType: 'text/plain',
        file,
        presignedUrl: assetUploadURL.uploadUrl,
      });

      if (type === 'web_store_order_processor') {
        const existingSecret =
          secrets.find((secret) => secret.name === onboardingProcesses[type].secretName) ?? null;

        if (!existingSecret) {
          const createdSecret = await createSecretAsync({
            name: onboardingProcesses[type].secretName,
            description: 'Credentials to log into the web store',
            value: [
              {
                value: 'standard_user',
                key: 'username',
              },
              {
                value: 'secret_sauce',
                key: 'password',
              },
            ],
          });

          saveOnboardingProcess({
            secretId: createdSecret.name,
          });
        }
      }

      // 6. Setting up the schedule
      await createScheduleAsync({
        processId: createdProcess.id,
        schedule: {
          type: ScheduleType.Weekday,
          mon: true,
          tue: false,
          wed: false,
          thu: false,
          fri: false,
          sat: false,
          sun: false,
          timezone: 'Europe/Helsinki',
          timeOfDaySeconds: 32400,
          configuration: {
            triggerProcessesFromInputs: false,
          },
        } as Schedule,
      });

      // 7. "Starting the process run"
      await startProcessRunAsync({
        processId: createdProcess.id,
      });

      // 8. "Automation ready!"
    },
    [
      setCreatingOnboardingProcess,
      createProcessAsync,
      baseURL,
      toggleOnboardingCompletedDialog,
      navigate,
      editProcessAsync,
      currentUser.details?.email,
      assetsList.data,
      requestAssetUploadAsync,
      uploadAssetAsync,
      createScheduleAsync,
      startProcessRunAsync,
      taskPackagesList,
      createRobotAsync,
      linkPublicGithubAsync,
      createStepAsync,
      cloudWorker.type,
      cloudWorker.id,
      editStepAsync,
      createAssetAsync,
      secrets,
      createSecretAsync,
    ],
  );

  return {
    setup: (params: HandleSetupParams) => handleSetup(params),
  };
};

export const useGetOnboardingProcess = (processId?: string) => {
  const { storageValue: onboardingProcess } = useLocalStorage<{
    type?: string;
    processId?: string;
    taskPackageId?: string;
    assetId?: string;
    secretId?: string;
  }>({
    key: 'onboardingProcess',
    sync: true,
    initialValue: {},
  });

  return {
    onboardingProcess,
    isOnboardingProcess: onboardingProcess.processId === processId,
  };
};
